// Route-aware driver marker animation. Ports flutter_client's
// _TrackingMixin (lib/views/main/cubit/main_cubit_tracking.dart):
//   - updateAnimateOnce: 100ms ticks at a fixed cruise speed (~45 km/h),
//     advancing along the route polyline segment by segment.
//   - _getNextSpan: on each raw GPS sample, re-snap to the nearest route
//     index so the marker jumps to the driver's real progress and continues
//     animating from there.
//   - interpolatePoint / calculateBearing: linear lat/lon interpolation and
//     compass bearing (degrees clockwise from north) for icon rotation.
//
// Without a route the tracker degrades to holding the marker at the latest raw
// position with the last known bearing — the car still shows and rotates once
// movement provides a direction.

export type Pt = { lat: number; lon: number };
export type DriverTick = { lat: number; lon: number; bearing: number };

const INTERVAL_MS = 100;
const SPEED_KM_H = 45;

function toRad(d: number): number {
  return (d * Math.PI) / 180;
}
function toDeg(r: number): number {
  return (r * 180) / Math.PI;
}

export function distanceMeters(a: Pt, b: Pt): number {
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lon - a.lon);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h =
    sinDLat * sinDLat +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinDLng * sinDLng;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function interpolatePoint(a: Pt, b: Pt, t: number): Pt {
  return {
    lat: a.lat + t * (b.lat - a.lat),
    lon: a.lon + t * (b.lon - a.lon),
  };
}

// Compass bearing from a -> b, degrees clockwise from north (0 = N, 90 = E).
export function bearingDeg(a: Pt, b: Pt): number {
  const dLng = toRad(b.lon - a.lon);
  const y = Math.sin(dLng) * Math.cos(toRad(b.lat));
  const x =
    Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) -
    Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

export class DriverTracker {
  private route: Pt[] = [];
  private seg = 0;
  private progress = 0; // meters traveled into the current segment
  private raw: Pt | null = null;
  private lastBearing = 0;
  private timer: ReturnType<typeof setInterval> | null = null;

  onTick: ((tick: DriverTick) => void) | null = null;

  setRoute(pts: Pt[]): void {
    this.route = pts;
    this.seg = 0;
    this.progress = 0;
    this.snapToRaw();
  }

  setRaw(p: Pt): void {
    this.raw = p;
    this.snapToRaw();
  }

  private snapToRaw(): void {
    if (this.route.length === 0 || !this.raw) return;
    let nearest = 0;
    let best = Infinity;
    for (let i = 0; i < this.route.length; i++) {
      const d = distanceMeters(this.raw, this.route[i]);
      if (d < best) {
        best = d;
        nearest = i;
      }
    }
    // Keep a segment to animate into; clamp to the last valid segment start.
    this.seg = Math.min(nearest, this.route.length - 2);
    this.progress = 0;
  }

  start(): void {
    this.stop();
    this.timer = setInterval(() => this.step(), INTERVAL_MS);
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private step(): void {
    if (!this.onTick) return;

    // No road geometry: hold the marker at the latest raw position. The car
    // still rotates once a bearing has been observed.
    if (this.route.length < 2) {
      if (this.raw) this.onTick({ ...this.raw, bearing: this.lastBearing });
      return;
    }

    const speedMps = (SPEED_KM_H * 1000) / 3600;
    const stepMeters = (speedMps * INTERVAL_MS) / 1000;
    this.progress += stepMeters;

    // Advance whole segments consumed by this step.
    while (this.seg < this.route.length - 1) {
      const segLen = distanceMeters(this.route[this.seg], this.route[this.seg + 1]);
      if (segLen === 0 || this.progress < segLen) break;
      this.progress -= segLen;
      this.seg++;
    }

    // Reached the end of the route — park at the destination, keep bearing.
    if (this.seg >= this.route.length - 1) {
      const end = this.route[this.route.length - 1];
      const prev = this.route[this.route.length - 2];
      this.lastBearing = bearingDeg(prev, end);
      this.onTick({ ...end, bearing: this.lastBearing });
      return;
    }

    const a = this.route[this.seg];
    const b = this.route[this.seg + 1];
    const segLen = distanceMeters(a, b);
    const t = segLen > 0 ? this.progress / segLen : 0;
    const pos = interpolatePoint(a, b, t);
    this.lastBearing = bearingDeg(a, b);
    this.onTick({ ...pos, bearing: this.lastBearing });
  }
}
